import React, { useState, useEffect, useRef } from 'react';
import Icons from './Icons';
import { MovingCarCard, FeaturedCarCard } from './CarCard';
import { fallbackCars } from '../data/sampleCars';

// Custom hook for responsive items per view
const useResponsiveItemsPerView = () => {
  const [itemsPerView, setItemsPerView] = useState(3);
  const [cardWidth, setCardWidth] = useState('w-1/3');

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setItemsPerView(1);
        setCardWidth('w-full');
      } else if (width < 1024) {
        setItemsPerView(2);
        setCardWidth('w-1/2');
      } else {
        setItemsPerView(3);
        setCardWidth('w-1/3');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { itemsPerView, cardWidth };
};

// Moving Car Slider Section
export const MovingCarSlider = ({ cars, isDark }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { itemsPerView, cardWidth } = useResponsiveItemsPerView();
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Math.max(1, cars.length - itemsPerView + 1));
    }, 3000);
    return () => clearInterval(interval);
  }, [cars.length, itemsPerView]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % Math.max(1, cars.length - itemsPerView + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + Math.max(1, cars.length - itemsPerView + 1)) % Math.max(1, cars.length - itemsPerView + 1));
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
  };

  return (
    <section className={`relative py-8 md:py-16 overflow-hidden transition-colors duration-300 ${isDark ? 'bg-[#0d0d0d]' : 'bg-gray-100'}`}>
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent"></div>
        <div className="absolute top-1/2 left-0 w-32 h-32 bg-gradient-to-r from-[#c9a962]/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-0 w-32 h-32 bg-gradient-to-l from-[#c9a962]/10 to-transparent rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative z-10 container-custom px-4">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <span className="text-[#c9a962] text-xs md:text-sm font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] animate-pulse">Featured Collection</span>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-3 md:mt-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Explore Our <span className="text-[#c9a962]">Premium Fleet</span>
          </h2>
          <p className={`mt-3 md:mt-4 max-w-2xl mx-auto text-sm md:text-base px-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Discover our exclusive collection of luxury vehicles for your perfect Udaipur experience
          </p>
        </div>
        
        {/* Slider Container */}
        <div className="relative">
          {/* Navigation Arrows - Hidden on mobile */}
          <button 
            onClick={prevSlide}
            className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 lg:w-12 h-10 lg:h-12 rounded-full items-center justify-center transition-all duration-300 -ml-4 lg:-ml-6 ${isDark ? 'bg-[#1a1a1a] text-white hover:bg-[#c9a962] hover:text-black' : 'bg-white text-gray-900 hover:bg-[#c9a962] hover:text-black shadow-lg'}`}
          >
            <Icons.ArrowLeft />
          </button>
          <button 
            onClick={nextSlide}
            className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 lg:w-12 h-10 lg:h-12 rounded-full items-center justify-center transition-all duration-300 -mr-4 lg:-mr-6 ${isDark ? 'bg-[#1a1a1a] text-white hover:bg-[#c9a962] hover:text-black' : 'bg-white text-gray-900 hover:bg-[#c9a962] hover:text-black shadow-lg'}`}
          >
            <Icons.ArrowRight />
          </button>
          
          {/* Car Cards */}
          <div 
            className="overflow-hidden mx-0 md:mx-8"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="flex gap-4 md:gap-6 transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
            >
              {cars.map((car, index) => (
                <div 
                  key={car.id} 
                  className={`flex-shrink-0 ${cardWidth} min-w-0 group ${index === currentIndex ? 'scale-[1.02]' : 'scale-100'} transition-all duration-500`}
                >
                  <MovingCarCard car={car} isDark={isDark} />
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-6 md:mt-8">
          {Array.from({ length: Math.max(1, cars.length - itemsPerView + 1) }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-[#c9a962] w-6 md:w-8' 
                  : isDark 
                    ? 'bg-gray-700 hover:bg-[#c9a962]/50' 
                    : 'bg-gray-300 hover:bg-[#c9a962]/50'
              }`}
            />
          ))}
        </div>
        
        {/* View All Link */}
        <div className="text-center mt-8 md:mt-10">
          <a href="/luxury-cars" className={`inline-flex items-center gap-2 px-6 md:px-8 py-2.5 md:py-3 border-2 border-[#c9a962] text-[#c9a962] text-sm md:text-base font-semibold uppercase tracking-wider hover:bg-[#c9a962] hover:text-black transition-all duration-300 rounded-lg ${isDark ? '' : 'bg-white'}`}>
            View All Cars <Icons.ArrowRight />
          </a>
        </div>
      </div>
    </section>
  );
};

// Luxury Cars Section
export const LuxuryCarsSection = ({ cars, isDark, openBookingModal, limit = 0 }) => {
  const allCars = cars && cars.length > 0 ? cars : fallbackCars;
  const displayCars = limit > 0 ? allCars.slice(0, limit) : allCars;
  const [hoveredCard, setHoveredCard] = useState(null);
  const [filterTransmission, setFilterTransmission] = useState('all');
  const [filterFuel, setFilterFuel] = useState('all');
  const [filterSeats, setFilterSeats] = useState('all');

  const filteredCars = displayCars.filter(car => {
    const matchTransmission = filterTransmission === 'all' || car.transmission?.toLowerCase() === filterTransmission.toLowerCase();
    const matchFuel = filterFuel === 'all' || car.fuel_type?.toLowerCase() === filterFuel.toLowerCase();
    const matchSeats = filterSeats === 'all' || car.seating_capacity?.toString() === filterSeats;
    return matchTransmission && matchFuel && matchSeats;
  });

  return (
    <section id="premium-collection" className={`py-12 md:py-24 relative overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#0d0d0d]' : 'bg-gradient-to-b from-gray-50 to-white'}`}>
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Top and Bottom Lines */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent"></div>
        
        {/* Floating Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#c9a962]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#c9a962]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        {/* Car Silhouette Pattern */}
        <div className={`absolute inset-0 ${isDark ? 'opacity-5' : 'opacity-[0.03]'}`} style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 40'%3E%3Cpath d='M20 30 Q30 10 50 10 Q70 10 80 30 L90 30 L90 35 L10 35 L10 30 Z' fill='${isDark ? '%23fff' : '%23000'}'/%3E%3C/svg%3E")`,
          backgroundSize: '150px 60px',
          backgroundRepeat: 'repeat'
        }}></div>
      </div>
      
      {/* Header */}
      <div className="relative z-10 container-custom px-4">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-[#c9a962]/10 border border-[#c9a962]/20 mb-3 md:mb-4">
            <span className="text-base md:text-xl">🚘</span>
            <span className="text-[#c9a962] text-xs md:text-sm font-semibold uppercase tracking-widest">Premium Collection</span>
          </div>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Luxury <span className="text-[#c9a962]">Cars</span>
          </h2>
          <p className={`mt-3 md:mt-4 max-w-2xl mx-auto text-sm md:text-base lg:text-lg px-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Premium vehicles for unforgettable experiences in Udaipur
          </p>
          
          {/* Quick Stats */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-6 mt-6 md:mt-8">
            <div className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow'}`}>
              <span className="text-[#c9a962] text-sm md:text-base">🚗</span>
              <span className={`font-semibold text-xs md:text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{allCars.length}+ Cars</span>
            </div>
            <div className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow'}`}>
              <span className="text-[#c9a962] text-sm md:text-base">⭐</span>
              <span className={`font-semibold text-xs md:text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Premium Fleet</span>
            </div>
            <div className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow'}`}>
              <span className="text-[#c9a962] text-sm md:text-base">✓</span>
              <span className={`font-semibold text-xs md:text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Verified & Insured</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-8 md:mb-12">
          <div className="relative group">
            <select 
              value={filterTransmission}
              onChange={(e) => setFilterTransmission(e.target.value)}
              className={`appearance-none px-3 md:px-6 py-2 md:py-3 pr-8 md:pr-10 rounded-lg md:rounded-xl text-xs md:text-base font-semibold transition-all duration-300 cursor-pointer ${
                filterTransmission !== 'all' 
                  ? 'bg-[#c9a962] text-black' 
                  : isDark ? 'bg-[#1a1a1a] text-white hover:bg-[#252525]' : 'bg-white text-gray-900 shadow hover:shadow-lg'
              }`}
            >
              <option value="all">All Transmission</option>
              <option value="automatic">Automatic</option>
              <option value="manual">Manual</option>
            </select>
            <div className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className={`w-4 h-4 md:w-5 md:h-5 ${filterTransmission !== 'all' ? 'text-black' : isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          
          <div className="relative group">
            <select 
              value={filterFuel}
              onChange={(e) => setFilterFuel(e.target.value)}
              className={`appearance-none px-3 md:px-6 py-2 md:py-3 pr-8 md:pr-10 rounded-lg md:rounded-xl text-xs md:text-base font-semibold transition-all duration-300 cursor-pointer ${
                filterFuel !== 'all' 
                  ? 'bg-[#c9a962] text-black' 
                  : isDark ? 'bg-[#1a1a1a] text-white hover:bg-[#252525]' : 'bg-white text-gray-900 shadow hover:shadow-lg'
              }`}
            >
              <option value="all">All Fuel Types</option>
              <option value="petrol">Petrol</option>
              <option value="diesel">Diesel</option>
              <option value="electric">Electric</option>
              <option value="hybrid">Hybrid</option>
            </select>
            <div className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className={`w-4 h-4 md:w-5 md:h-5 ${filterFuel !== 'all' ? 'text-black' : isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          
          <div className="relative group">
            <select 
              value={filterSeats}
              onChange={(e) => setFilterSeats(e.target.value)}
              className={`appearance-none px-3 md:px-6 py-2 md:py-3 pr-8 md:pr-10 rounded-lg md:rounded-xl text-xs md:text-base font-semibold transition-all duration-300 cursor-pointer ${
                filterSeats !== 'all' 
                  ? 'bg-[#c9a962] text-black' 
                  : isDark ? 'bg-[#1a1a1a] text-white hover:bg-[#252525]' : 'bg-white text-gray-900 shadow hover:shadow-lg'
              }`}
            >
              <option value="all">All Seats</option>
              <option value="4">4 Seats</option>
              <option value="5">5 Seats</option>
              <option value="7">7 Seats</option>
              <option value="8">8 Seats</option>
            </select>
            <div className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className={`w-4 h-4 md:w-5 md:h-5 ${filterSeats !== 'all' ? 'text-black' : isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          
          {/* Clear Filters Button */}
          {(filterTransmission !== 'all' || filterFuel !== 'all' || filterSeats !== 'all') && (
            <button
              onClick={() => { setFilterTransmission('all'); setFilterFuel('all'); setFilterSeats('all'); }}
              className="px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-semibold bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-all duration-300"
            >
              Clear Filters ✕
            </button>
          )}
        </div>

        {/* Car Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {filteredCars.map((car, index) => (
            <div 
              key={car.id}
              className={`group relative overflow-hidden rounded-xl md:rounded-2xl lg:rounded-3xl transition-all duration-500 ${
                hoveredCard === index ? 'scale-[1.02] z-10 shadow-2xl shadow-[#c9a962]/20' : 'shadow-lg md:shadow-xl'
              } ${isDark ? 'bg-[#1a1a1a]' : 'bg-white'}`}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Image Section */}
              <div className="relative h-48 sm:h-52 md:h-56 lg:h-64 overflow-hidden">
                <img 
                  src={car.image} 
                  alt={car.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
                
                {/* Featured Badge */}
                <div className="absolute top-2 md:top-4 left-2 md:left-4 flex items-center gap-1 md:gap-2">
                  <span className="bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black text-[10px] md:text-xs font-bold px-2 md:px-4 py-0.5 md:py-1.5 uppercase tracking-wider rounded-full shadow-lg flex items-center gap-1">
                    <span className="animate-pulse">⭐</span> Featured
                  </span>
                </div>
                
                {/* Quick Actions - Hidden on mobile */}
                <div className="hidden md:flex absolute top-4 right-4 flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-4 group-hover:translate-x-0">
                  <button className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/20 backdrop-blur-sm text-white hover:bg-[#c9a962] hover:text-black' : 'bg-black/50 backdrop-blur-sm text-white hover:bg-[#c9a962] hover:text-black'} transition-all duration-300`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                  <button className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/20 backdrop-blur-sm text-white hover:bg-[#c9a962] hover:text-black' : 'bg-black/50 backdrop-blur-sm text-white hover:bg-[#c9a962] hover:text-black'} transition-all duration-300`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>
                
                {/* Price Tag */}
                <div className="absolute bottom-2 md:bottom-4 right-2 md:right-4 bg-black/80 backdrop-blur-sm px-3 md:px-5 py-1.5 md:py-2.5 rounded-lg md:rounded-xl transform transition-all duration-300 group-hover:scale-110 group-hover:bg-[#c9a962]">
                  <div className="flex items-baseline gap-0.5 md:gap-1">
                    <span className="text-base md:text-xl lg:text-2xl font-bold text-[#c9a962] group-hover:text-black">₹{car.price_per_day?.toLocaleString()}</span>
                    <span className="text-[10px] md:text-sm text-gray-400 group-hover:text-black/70">/day</span>
                  </div>
                </div>
                
                {/* Car Name Overlay */}
                <div className="absolute bottom-2 md:bottom-4 left-2 md:left-4">
                  <h3 className="text-sm md:text-lg lg:text-xl font-bold text-white drop-shadow-lg line-clamp-1">{car.name}</h3>
                </div>
              </div>
              
              {/* Content Section */}
              <div className="p-4 md:p-6">
                {/* Specs Grid */}
                <div className="grid grid-cols-3 gap-2 md:gap-3 mb-3 md:mb-5">
                  <div className={`text-center p-2 md:p-3 rounded-lg md:rounded-xl ${isDark ? 'bg-[#252525]' : 'bg-gray-50'}`}>
                    <div className="text-[#c9a962] mb-0.5 md:mb-1">
                      <svg className="w-4 h-4 md:w-5 md:h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                    </div>
                    <span className={`text-[10px] md:text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{car.transmission}</span>
                  </div>
                  <div className={`text-center p-2 md:p-3 rounded-lg md:rounded-xl ${isDark ? 'bg-[#252525]' : 'bg-gray-50'}`}>
                    <div className="text-[#c9a962] mb-0.5 md:mb-1">
                      <svg className="w-4 h-4 md:w-5 md:h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                      </svg>
                    </div>
                    <span className={`text-[10px] md:text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{car.fuel_type}</span>
                  </div>
                  <div className={`text-center p-2 md:p-3 rounded-lg md:rounded-xl ${isDark ? 'bg-[#252525]' : 'bg-gray-50'}`}>
                    <div className="text-[#c9a962] mb-0.5 md:mb-1">
                      <svg className="w-4 h-4 md:w-5 md:h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <span className={`text-[10px] md:text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{car.seating_capacity} Seats</span>
                  </div>
                </div>
                
                <p className={`text-xs md:text-sm mb-3 md:mb-5 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {car.description}
                </p>
                
                {/* Book Now Button */}
                <button 
                  onClick={() => openBookingModal(car)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 md:py-4 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black text-sm md:text-base font-bold uppercase tracking-wider rounded-lg md:rounded-xl hover:shadow-xl hover:shadow-[#c9a962]/30 transition-all duration-300 group-hover:scale-[1.02]"
                >
                  <span>Book Now</span>
                  <svg className="w-4 h-4 md:w-5 md:h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
              </div>
              
              {/* Bottom Accent Line */}
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#c9a962] to-[#a08040] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
            </div>
          ))}
        </div>
        
        {/* No Results */}
        {filteredCars.length === 0 && (
          <div className={`text-center py-12 md:py-16 rounded-xl md:rounded-2xl ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
            <div className="text-4xl md:text-6xl mb-4">🚗</div>
            <h3 className={`text-lg md:text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>No cars found</h3>
            <p className={`text-sm md:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Try adjusting your filters</p>
            <button
              onClick={() => { setFilterTransmission('all'); setFilterFuel('all'); setFilterSeats('all'); }}
              className="mt-4 px-4 md:px-6 py-2 bg-[#c9a962] text-black text-sm md:text-base font-semibold rounded-lg hover:bg-[#b89952] transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}
        
        {/* View All Button */}
        {limit > 0 && allCars.length > limit && (
          <div className="text-center mt-8 md:mt-12">
            <a 
              href="/luxury-cars" 
              className={`inline-flex items-center gap-2 md:gap-3 px-6 md:px-10 py-3 md:py-4 text-sm md:text-base font-bold uppercase tracking-wider rounded-lg md:rounded-xl transition-all duration-300 hover:scale-105 ${
                isDark 
                  ? 'bg-[#1a1a1a] text-white border-2 border-[#c9a962] hover:bg-[#c9a962] hover:text-black' 
                  : 'bg-white text-gray-900 border-2 border-[#c9a962] hover:bg-[#c9a962] hover:text-black shadow-lg'
              }`}
            >
              <span>View All {allCars.length} Cars</span>
              <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

// Featured Collection Slider Section
export const FeaturedCollection = ({ cars, isDark, openBookingModal }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { itemsPerView, cardWidth } = useResponsiveItemsPerView();
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  
  const displayCars = cars && cars.length > 0 ? cars : fallbackCars;
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Math.max(1, displayCars.length - itemsPerView + 1));
    }, 3000);
    return () => clearInterval(interval);
  }, [displayCars.length, itemsPerView]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % Math.max(1, displayCars.length - itemsPerView + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + Math.max(1, displayCars.length - itemsPerView + 1)) % Math.max(1, displayCars.length - itemsPerView + 1));
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
  };

  return (
    <section className={`py-8 md:py-16 overflow-hidden transition-colors duration-300 ${isDark ? 'bg-[#0d0d0d]' : 'bg-gray-100'}`}>
      {/* Header */}
      <div className="container-custom px-4">
        <div className="text-center mb-8 md:mb-12">
          <span className="text-[#c9a962] text-xs md:text-sm font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em]">Featured Collection</span>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-3 md:mt-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Explore Our Premium Fleet
          </h2>
          <p className={`mt-3 md:mt-4 max-w-2xl mx-auto text-sm md:text-base px-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Discover our exclusive collection of luxury vehicles for your perfect Udaipur experience
          </p>
        </div>
        
        {/* Slider Container */}
        <div className="relative">
          {/* Navigation Arrows - Hidden on mobile */}
          <button 
            onClick={prevSlide}
            className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 lg:w-12 h-10 lg:h-12 rounded-full items-center justify-center transition-all duration-300 -ml-4 lg:-ml-6 ${isDark ? 'bg-[#1a1a1a] text-white hover:bg-[#c9a962] hover:text-black' : 'bg-white text-gray-900 hover:bg-[#c9a962] hover:text-black shadow-lg'}`}
          >
            <Icons.ArrowLeft />
          </button>
          <button 
            onClick={nextSlide}
            className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 lg:w-12 h-10 lg:h-12 rounded-full items-center justify-center transition-all duration-300 -mr-4 lg:-mr-6 ${isDark ? 'bg-[#1a1a1a] text-white hover:bg-[#c9a962] hover:text-black' : 'bg-white text-gray-900 hover:bg-[#c9a962] hover:text-black shadow-lg'}`}
          >
            <Icons.ArrowRight />
          </button>
          
          {/* Car Cards with touch support */}
          <div 
            className="overflow-hidden mx-0 md:mx-8"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="flex gap-4 md:gap-6 transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
            >
              {displayCars.map((car, index) => (
                <div 
                  key={car.id} 
                  className={`flex-shrink-0 ${cardWidth} min-w-0 group ${index === currentIndex ? 'scale-[1.02]' : 'scale-100'} transition-all duration-500`}
                >
                  <FeaturedCarCard car={car} isDark={isDark} openBookingModal={openBookingModal} />
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-6 md:mt-8">
          {Array.from({ length: Math.max(1, displayCars.length - itemsPerView + 1) }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-[#c9a962] w-6 md:w-8' 
                  : isDark 
                    ? 'bg-gray-700 hover:bg-[#c9a962]/50' 
                    : 'bg-gray-300 hover:bg-[#c9a962]/50'
              }`}
            />
          ))}
        </div>
        
        <div className="text-center mt-8 md:mt-10">
          <a href="/luxury-cars" className={`inline-flex items-center gap-2 px-6 md:px-8 py-2.5 md:py-3 border-2 border-[#c9a962] text-[#c9a962] text-sm md:text-base font-semibold uppercase tracking-wider hover:bg-[#c9a962] hover:text-black transition-all duration-300 rounded-lg ${isDark ? '' : 'bg-white'}`}>
            View All Cars <Icons.ArrowRight />
          </a>
        </div>
      </div>
    </section>
  );
};

// Stats Section
export const StatsSection = ({ isDark }) => {
  const stats = [
    { value: '25+', label: 'Premium Cars', icon: '🚗', description: 'Luxury & Economy' },
    { value: '500+', label: 'Happy Customers', icon: '😊', description: 'Satisfied Clients' },
    { value: '5+', label: 'Years Experience', icon: '⭐', description: 'Industry Expertise' },
    { value: '24/7', label: 'Support Available', icon: '🎧', description: 'Always Here to Help' },
    { value: '1000+', label: 'Trips Completed', icon: '🛣️', description: 'Memorable Journeys' },
    { value: '50+', label: 'Destinations', icon: '🌍', description: 'Across Rajasthan' }
  ];

  return (
    <section className={`py-12 sm:py-16 md:py-20 relative overflow-hidden transition-colors duration-300 ${isDark ? 'bg-[#0d0d0d]' : 'bg-gray-50'}`}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent"></div>
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-gradient-to-r from-[#c9a962]/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-gradient-to-l from-[#c9a962]/10 to-transparent rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative z-10 container-custom px-3 sm:px-4">
        <div className="text-center mb-8 sm:mb-12">
          <span className="text-[#c9a962] text-[10px] sm:text-sm font-semibold uppercase tracking-[0.2em] sm:tracking-[0.3em]">Our Achievements</span>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-2 sm:mt-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Why Choose <span className="text-[#c9a962]">Brothers</span>
          </h2>
          <p className={`mt-2 sm:mt-4 max-w-2xl mx-auto text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Experience luxury car rental with Udaipur's most trusted premium service
          </p>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 md:gap-6">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className={`relative group text-center p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#c9a962]/20 ${isDark ? 'bg-[#1a1a1a] hover:bg-[#222]' : 'bg-white shadow-lg hover:shadow-2xl'}`}
            >
              <div className="relative mb-2 sm:mb-4">
                <div className={`text-2xl sm:text-3xl md:text-5xl transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12`}>
                  {stat.icon}
                </div>
                <div className={`absolute inset-0 bg-[#c9a962] rounded-full blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-500`}></div>
              </div>
              
              <div className={`text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-[#c9a962] mb-1 sm:mb-2 transition-all duration-500 group-hover:text-white ${isDark ? '' : 'text-gray-900'}`}>
                {stat.value}
              </div>
              
              <div className={`font-semibold text-xs sm:text-sm md:text-lg mb-0.5 sm:mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {stat.label}
              </div>
              
              <div className={`text-[10px] sm:text-xs md:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {stat.description}
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-8 sm:mt-12">
          <a href="/contact" className={`inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black font-semibold uppercase tracking-wider text-sm sm:text-base hover:shadow-lg hover:shadow-[#c9a962]/30 transition-all duration-300 rounded-lg cursor-pointer hover:scale-105`}>
            Start Your Journey <Icons.ArrowRight />
          </a>
        </div>
      </div>
    </section>
  );
};

// Services Section
export const ServicesSection = ({ isDark }) => {
  const [activeService, setActiveService] = useState(0);
  const [isHovered, setIsHovered] = useState(null);

  // WhatsApp booking function for services
  const handleServiceBooking = (service) => {
    const message = `*Brothers Car Rental - Service Inquiry*

Hello! I'm interested in your *${service.title}* service.

Pricing: ${service.price}

Please provide more information about availability and booking process.

Thank you!`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/919602360369?text=${encodedMessage}`, '_blank');
  };

  const services = [
    { 
      title: 'Self-Drive Rentals', 
      description: 'Freedom to explore Udaipur at your own pace with our premium self-drive cars. Choose from our luxury fleet and enjoy complete independence on your journey.',
      icon: '🚗',
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-gradient-to-br from-amber-500/20 to-orange-500/20',
      features: ['No Driver Required', '24/7 Roadside Support', 'Flexible Pickup Locations', 'Unlimited Kilometers'],
      price: 'Starting ₹1,500/day',
      popular: true
    },
    { 
      title: 'Airport Transfers', 
      description: 'Comfortable and timely pickups and drop-offs from Udaipur Airport and railway stations. Begin your journey stress-free with our premium transfer service.',
      icon: '✈️',
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-gradient-to-br from-blue-500/20 to-indigo-500/20',
      features: ['Real-time Flight Tracking', 'Meet & Greet Service', 'Luggage Assistance', 'AC Vehicles'],
      price: 'Starting ₹1100/trip',
      popular: false
    },
    { 
      title: 'Corporate Rentals', 
      description: 'Professional car rentals for corporate events, business meetings, and executive travel. Make a lasting impression with our premium fleet.',
      icon: '💼',
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20',
      features: ['Business Ready Fleet', 'Professional Chauffeurs', 'Monthly Invoice Billing', 'Dedicated Account Manager'],
      price: 'Custom Pricing',
      popular: false
    },
    { 
      title: 'Wedding Car Rentals', 
      description: 'Make your special day truly memorable with our luxury wedding car collection. Arrive in style and elegance on your big day.',
      icon: '💒',
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-gradient-to-br from-pink-500/20 to-rose-500/20',
      features: ['Decorated Luxury Cars', 'Flower Decoration', 'Chauffeur Service', 'Multiple Car Options'],
      price: 'Starting ₹15,000',
      popular: true
    },
    { 
      title: 'Tour Packages', 
      description: 'Expertly curated tour packages covering all major attractions in and around Udaipur. Explore the city of lakes with experienced local guides.',
      icon: '🗺️',
      color: 'from-purple-500 to-violet-500',
      bgColor: 'bg-gradient-to-br from-purple-500/20 to-violet-500/20',
      features: ['Expert Local Guides', 'Custom Itineraries', 'All-Inclusive Packages', 'Multi-day Tours'],
      price: 'Starting ₹3,500/day',
      popular: false
    },
    { 
      title: 'Long-Term Rentals', 
      description: 'Economical long-term rental plans for extended stays in Udaipur. Best value for business travelers and tourists planning longer visits.',
      icon: '📅',
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20',
      features: ['Best Monthly Rates', 'Free Maintenance', 'Flexible Terms', 'Vehicle Replacement'],
      price: 'Starting ₹35,000/month',
      popular: false
    }
  ];

  return (
    <section className={`py-12 sm:py-16 md:py-24 relative overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#0d0d0d]' : 'bg-gradient-to-b from-gray-50 to-gray-100'}`}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Top and Bottom Lines */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent"></div>
        
        {/* Floating Orbs - Hidden on mobile */}
        <div className="hidden sm:block absolute top-20 left-10 w-72 h-72 bg-[#c9a962]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="hidden sm:block absolute bottom-20 right-10 w-96 h-96 bg-[#c9a962]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#c9a962]/5 rounded-full blur-3xl"></div>
        
        {/* Grid Pattern */}
        <div className={`absolute inset-0 ${isDark ? 'opacity-5' : 'opacity-10'}`} style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDark ? '#fff' : '#000'} 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative z-10 container-custom px-3 sm:px-4">
        {/* Header Section */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#c9a962]/10 border border-[#c9a962]/20 mb-3 sm:mb-4">
            <span className="text-base sm:text-xl">⭐</span>
            <span className="text-[#c9a962] text-[10px] sm:text-sm font-semibold uppercase tracking-wider">Premium Services</span>
          </div>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Our <span className="text-[#c9a962]">Premium</span> Services
          </h2>
          <p className={`mt-3 sm:mt-6 max-w-3xl mx-auto text-sm sm:text-base md:text-lg leading-relaxed px-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Discover our comprehensive range of premium car rental services designed to make your Udaipur experience truly unforgettable
          </p>
          
          {/* Stats Bar */}
          <div className={`flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8 mt-6 sm:mt-10 p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl ${isDark ? 'bg-[#1a1a1a]/50' : 'bg-white/50'} backdrop-blur-sm border ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#c9a962]">6+</div>
              <div className={`text-[10px] sm:text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Service Types</div>
            </div>
            <div className="hidden sm:block w-px h-8 md:h-12 bg-gray-700"></div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#c9a962]">500+</div>
              <div className={`text-[10px] sm:text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Happy Clients</div>
            </div>
            <div className="hidden sm:block w-px h-8 md:h-12 bg-gray-700"></div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#c9a962]">24/7</div>
              <div className={`text-[10px] sm:text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Support</div>
            </div>
            <div className="hidden sm:block w-px h-8 md:h-12 bg-gray-700"></div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#c9a962]">100%</div>
              <div className={`text-[10px] sm:text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Satisfaction</div>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {services.map((service, index) => (
            <div 
              key={index} 
              className={`group relative rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-500 ${
                isHovered === index ? 'scale-[1.02] z-10' : 'scale-100'
              } ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-xl'}`}
              onMouseEnter={() => setIsHovered(index)}
              onMouseLeave={() => setIsHovered(null)}
            >
              {/* Popular Badge */}
              {service.popular && (
                <div className="absolute top-3 sm:top-4 right-3 sm:right-4 z-20">
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black text-[10px] sm:text-xs font-bold rounded-full shadow-lg">
                    ⭐ POPULAR
                  </span>
                </div>
              )}

              {/* Gradient Overlay on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
              
              {/* Card Content */}
              <div className="relative p-4 sm:p-6 md:p-8">
                {/* Icon Container */}
                <div className="relative mb-4 sm:mb-6">
                  <div className={`w-14 h-14 sm:w-16 md:w-20 sm:h-16 md:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center text-2xl sm:text-3xl md:text-4xl shadow-xl transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 group-hover:shadow-2xl`}>
                    {service.icon}
                  </div>
                  {/* Glow Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} rounded-xl sm:rounded-2xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500`}></div>
                </div>

                {/* Title & Description */}
                <h3 className={`text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 transition-all duration-300 ${isDark ? 'text-white group-hover:text-[#c9a962]' : 'text-gray-900 group-hover:text-[#c9a962]'}`}>
                  {service.title}
                </h3>
                
                <p className={`leading-relaxed mb-4 sm:mb-6 text-xs sm:text-sm md:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {service.description}
                </p>

                {/* Features List */}
                <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br ${service.color} flex items-center justify-center flex-shrink-0`}>
                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className={`text-[10px] sm:text-xs md:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Price Tag */}
                <div className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl ${isDark ? 'bg-[#252525]' : 'bg-gray-100'}`}>
                  <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>💰</span>
                  <span className={`text-xs sm:text-sm md:text-base font-bold ${isDark ? 'text-[#c9a962]' : 'text-[#a08040]'}`}>{service.price}</span>
                </div>

                {/* Book Now Button */}
                <button 
                  onClick={() => handleServiceBooking(service)}
                  className={`w-full mt-4 sm:mt-6 py-3 sm:py-4 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 ${
                  isDark 
                    ? 'bg-[#252525] text-white hover:bg-gradient-to-r hover:from-[#c9a962] hover:to-[#a08040] hover:text-black' 
                    : 'bg-gray-100 text-gray-900 hover:bg-gradient-to-r hover:from-[#c9a962] hover:to-[#a08040] hover:text-black'
                } group-hover:shadow-lg group-hover:shadow-[#c9a962]/20`}>
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Book Now
                  </span>
                </button>
              </div>

              {/* Bottom Accent Line */}
              <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${service.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-8 sm:mt-12 md:mt-16">
          <div className={`relative p-6 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl overflow-hidden ${isDark ? 'bg-gradient-to-r from-[#1a1a1a] to-[#252525]' : 'bg-white shadow-2xl'}`}>
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#c9a962] rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#c9a962] rounded-full blur-3xl"></div>
            </div>

            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="text-center lg:text-left">
                <h3 className={`text-2xl md:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Need a Custom Solution?
                </h3>
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Contact us for personalized packages tailored to your specific requirements
                </p>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <a 
                  href="tel:9602360369"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black font-bold rounded-xl hover:shadow-xl hover:shadow-[#c9a962]/30 transition-all duration-300 hover:scale-105"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Call: 96023 60369
                </a>
                <a 
                  href="/contact"
                  className={`inline-flex items-center gap-3 px-8 py-4 font-bold rounded-xl transition-all duration-300 hover:scale-105 ${
                    isDark 
                      ? 'bg-[#252525] text-white hover:bg-[#c9a962] hover:text-black' 
                      : 'bg-gray-100 text-gray-900 hover:bg-[#c9a962] hover:text-black'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Get Custom Quote
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
export const FAQSection = ({ isDark }) => {
  const [openIndex, setOpenIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', name: 'All Questions', icon: '📋' },
    { id: 'booking', name: 'Booking', icon: '📝' },
    { id: 'payment', name: 'Payment', icon: '💳' },
    { id: 'vehicle', name: 'Vehicle', icon: '🚗' },
    { id: 'policy', name: 'Policies', icon: '📄' }
  ];

  const faqs = [
    {
      question: 'What documents do I need to rent a car?',
      answer: 'You need a valid driving license (minimum 1 year old), Aadhar card or passport for identity proof, and a valid mobile number. For international travelers, a valid International Driving Permit (IDP) along with original driving license is required.',
      category: 'booking',
      icon: '📄',
      popular: true
    },
    {
      question: 'Is there a minimum rental period?',
      answer: 'Yes, the minimum rental period is 24 hours (1 day). We also offer hourly rentals for select vehicles within Udaipur city for minimum 4 hours. Long-term rentals (7+ days) come with special discounted rates.',
      category: 'booking',
      icon: '⏰',
      popular: true
    },
    {
      question: 'What is your cancellation policy?',
      answer: 'Free cancellation up to 24 hours before pickup. 50% charge for cancellations between 24-12 hours. No refund for cancellations less than 12 hours before pickup. In case of emergencies, please contact our support team.',
      category: 'policy',
      icon: '❌',
      popular: true
    },
 
    {
      question: 'Are your cars insured?',
      answer: 'Yes, all our vehicles are fully insured with comprehensive coverage including third-party insurance. You can drive with complete peace of mind. However, any damage due to negligence or violation of terms will be charged separately.',
      category: 'vehicle',
      icon: '🛡️',
      popular: true
    },
    {
      question: 'Can I extend my rental period?',
      answer: 'Absolutely! You can extend your rental by contacting us at least 1 hours before your scheduled return time, subject to vehicle availability. Extension charges will be calculated based on current rates.',
      category: 'booking',
      icon: '📅',
      popular: false
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept cash, all major credit/debit cards (Visa, Mastercard, RuPay), UPI payments (GPay, PhonePe, Paytm), and net banking. Corporate clients can also avail credit facilities subject to approval.',
      category: 'payment',
      icon: '💳',
      popular: false
    },
    {
      question: 'Is there a security deposit?',
      answer: 'Yes, a refundable security deposit is required which varies based on the car category: ₹5,000 for economy cars, ₹10,000 for premium cars, and ₹25,000 for luxury cars. The deposit is fully refundable within 24 hours of returning the car in good condition.',
      category: 'payment',
      icon: '💰',
      popular: true
    },
    {
      question: 'What happens in case of breakdown?',
      answer: 'We provide 24/7 roadside assistance across Rajasthan. In case of breakdown, call our helpline immediately. We will arrange for a replacement vehicle or on-spot repair within 2 hours in city limits.',
      category: 'vehicle',
      icon: '🔧',
      popular: false
    },
    {
      question: 'Can I drive outside Udaipur?',
      answer: 'Yes, you can drive anywhere within Rajasthan. For out-of-state travel, prior permission is required along with additional documentation. Extra charges may apply for interstate permits.',
      category: 'policy',
      icon: '🗺️',
      popular: false
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const popularFaqs = faqs.filter(faq => faq.popular);

  return (
    <section className={`py-12 sm:py-16 md:py-24 relative overflow-hidden transition-colors duration-300 ${isDark ? 'bg-[#0d0d0d]' : 'bg-gray-50'}`}>
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent"></div>
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#c9a962]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#c9a962]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 container-custom">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 px-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a962]/10 border border-[#c9a962]/20 mb-4">
            <span className="text-lg sm:text-xl">❓</span>
            <span className="text-[#c9a962] text-xs sm:text-sm font-semibold uppercase tracking-widest">Got Questions?</span>
          </div>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Frequently Asked <span className="text-[#c9a962]">Questions</span>
          </h2>
          <p className={`mt-3 sm:mt-4 max-w-2xl mx-auto text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Everything you need to know about our premium car rental services
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-8 sm:mb-10 px-4">
          <div className={`relative rounded-xl sm:rounded-2xl overflow-hidden ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
            <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search your question..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 text-sm sm:text-base outline-none ${isDark ? 'bg-transparent text-white placeholder-gray-500' : 'bg-transparent text-gray-900 placeholder-gray-400'}`}
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#c9a962]"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-10 px-4">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black shadow-lg shadow-[#c9a962]/30'
                  : isDark 
                    ? 'bg-[#1a1a1a] text-gray-300 hover:bg-[#252525] border border-gray-800' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow border border-gray-200'
              }`}
            >
              <span>{category.icon}</span>
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        {/* Popular Questions Quick Links */}
        {activeCategory === 'all' && !searchQuery && (
          <div className="max-w-4xl mx-auto mb-8 sm:mb-10 px-4">
            <div className={`p-4 sm:p-6 rounded-xl sm:rounded-2xl ${isDark ? 'bg-[#1a1a1a] border border-[#c9a962]/20' : 'bg-white shadow-lg border border-[#c9a962]/10'}`}>
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <span className="text-lg sm:text-xl">⭐</span>
                <h3 className={`font-semibold text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>Popular Questions</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {popularFaqs.map((faq, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      const faqIndex = faqs.findIndex(f => f.question === faq.question);
                      setOpenIndex(faqIndex);
                    }}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm transition-all duration-300 ${
                      isDark 
                        ? 'bg-[#c9a962]/10 text-[#c9a962] hover:bg-[#c9a962]/20' 
                        : 'bg-[#c9a962]/10 text-[#a08040] hover:bg-[#c9a962]/20'
                    }`}
                  >
                    {faq.icon} {faq.question.substring(0, 25)}...
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto px-4">
          {filteredFaqs.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {filteredFaqs.map((faq, index) => {
                const originalIndex = faqs.findIndex(f => f.question === faq.question);
                return (
                  <div
                    key={index}
                    className={`group rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-500 ${
                      openIndex === originalIndex 
                        ? 'ring-2 ring-[#c9a962] shadow-xl shadow-[#c9a962]/10' 
                        : ''
                    } ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}
                  >
                    <button
                      onClick={() => setOpenIndex(openIndex === originalIndex ? -1 : originalIndex)}
                      className={`w-full px-4 sm:px-6 py-4 sm:py-5 text-left flex items-start sm:items-center gap-3 sm:gap-4 transition-all duration-300 ${
                        isDark ? 'hover:bg-[#252525]' : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Icon */}
                      <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-xl sm:text-2xl transition-all duration-300 ${
                        openIndex === originalIndex
                          ? 'bg-gradient-to-br from-[#c9a962] to-[#a08040]'
                          : isDark ? 'bg-[#252525]' : 'bg-gray-100'
                      }`}>
                        {faq.icon}
                      </div>
                      
                      {/* Question */}
                      <span className={`flex-1 font-semibold text-left text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {faq.question}
                      </span>
                      
                      {/* Popular Badge */}
                      {faq.popular && (
                        <span className={`hidden md:inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                          isDark ? 'bg-[#c9a962]/20 text-[#c9a962]' : 'bg-[#c9a962]/10 text-[#a08040]'
                        }`}>
                          Popular
                        </span>
                      )}
                      
                      {/* Arrow */}
                      <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                        openIndex === originalIndex
                          ? 'bg-[#c9a962] text-black rotate-180'
                          : isDark ? 'bg-[#252525] text-gray-400' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    
                    {/* Answer */}
                    <div
                      className={`overflow-hidden transition-all duration-500 ease-in-out ${
                        openIndex === originalIndex ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className={`px-4 sm:px-6 pb-6 pt-2 sm:ml-16 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <div className={`p-4 rounded-xl ${isDark ? 'bg-[#0d0d0d]' : 'bg-gray-50'}`}>
                          <p className="leading-relaxed break-words whitespace-normal overflow-wrap-break-word">{faq.answer}</p>
                        </div>
                        
                        {/* Helpful Actions */}
                        <div className="flex items-center gap-3 sm:gap-4 mt-4">
                          <button className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm transition-colors ${
                            isDark ? 'text-gray-500 hover:text-[#c9a962]' : 'text-gray-500 hover:text-[#c9a962]'
                          }`}>
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                            </svg>
                            Helpful
                          </button>
                          <button className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm transition-colors ${
                            isDark ? 'text-gray-500 hover:text-[#c9a962]' : 'text-gray-500 hover:text-[#c9a962]'
                          }`}>
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                            </svg>
                            Share
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={`text-center py-16 rounded-2xl ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
              <div className="text-6xl mb-4">🔍</div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>No questions found</h3>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Try adjusting your search or filter</p>
              <button
                onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
                className="mt-4 px-6 py-2 bg-[#c9a962] text-black font-semibold rounded-lg hover:bg-[#b89952] transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Still Have Questions CTA */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className={`p-8 rounded-2xl text-center ${isDark ? 'bg-gradient-to-r from-[#1a1a1a] to-[#252525] border border-[#c9a962]/20' : 'bg-white shadow-xl'}`}>
            <div className="text-4xl mb-4">💬</div>
            <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Still have questions?</h3>
            <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Can't find the answer you're looking for? Our support team is here to help!</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:9602360369"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black font-semibold rounded-xl hover:shadow-lg hover:shadow-[#c9a962]/30 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call Us: 96023 60369
              </a>
              <a
                href="https://wa.me/919602360369"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-xl transition-all duration-300 ${
                  isDark 
                    ? 'bg-[#25D366] text-white hover:bg-[#22c55e]' 
                    : 'bg-[#25D366] text-white hover:bg-[#22c55e]'
                }`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
export const HowItWorks = ({ isDark }) => {
  const steps = [
    { title: 'Choose Your Car', description: 'Browse our premium fleet and select the perfect car for your journey.', icon: Icons.Car, color: 'bg-gradient-to-br from-amber-500 to-orange-500' },
    { title: 'Book Online or Call', description: 'Complete your booking through our simple online form or call us directly at 96023 60369.', icon: Icons.Check, color: 'bg-gradient-to-br from-emerald-500 to-teal-500' },
    { title: 'Pick Up & Drive', description: 'Collect your car at your preferred location and start your unforgettable journey in Udaipur.', icon: Icons.Key, color: 'bg-gradient-to-br from-blue-500 to-indigo-500' }
  ];
  
  return (
    <section className={`py-24 relative overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#c9a962]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#c9a962]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>
      
      <div className="relative z-10 container-custom">
        <div className="text-center mb-16">
          <span className={`inline-block py-2 px-6 rounded-full ${isDark ? 'bg-[#c9a962]/10 text-[#c9a962]' : 'bg-[#c9a962]/10 text-[#c9a962]'} text-sm font-semibold uppercase tracking-widest mb-4 animate-fade-in`}>Simple Process</span>
          <h2 className={`text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>How It <span className="text-[#c9a962] relative">Works<span className="absolute -bottom-2 left-0 w-full h-1 bg-[#c9a962]"></span></span></h2>
          <p className={`max-w-2xl mx-auto leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'} text-lg`}>Renting a luxury car has never been easier - just 3 simple steps to your perfect journey</p>
          <div className="w-24 h-1 bg-[#c9a962] mx-auto mt-8 relative overflow-hidden rounded-full">
            <div className="absolute inset-0 bg-[#c9a962] animate-shimmer"></div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className={`group relative text-center p-10 rounded-3xl transition-all duration-500 hover:scale-105 hover:-translate-y-2 ${isDark ? 'bg-[#0d0d0d] hover:bg-[#1a1a1a]' : 'bg-white hover:bg-gray-50'} shadow-xl hover:shadow-2xl`}
              style={{ animationDelay: `${index * 200}ms` }}
            >
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                <div className={`relative ${step.color} w-16 h-16 rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300`}>
                  <step.icon className="w-8 h-8 text-white" />
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#c9a962] text-black rounded-full flex items-center justify-center font-bold text-lg shadow-lg animate-bounce">
                    {index + 1}
                  </div>
                </div>
              </div>
              
              {index < 2 && (
                <div className="hidden md:block absolute top-1/2 -right-6 transform -translate-y-1/2 z-10">
                  <div className="w-12 h-12 bg-[#c9a962] rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    <Icons.ArrowRight className="w-6 h-6 text-black" />
                  </div>
                </div>
              )}
              
              <h3 className={`text-2xl font-bold mb-4 mt-6 group-hover:text-[#c9a962] transition-colors duration-300 ${isDark ? 'text-white' : 'text-gray-900'}`}>{step.title}</h3>
              <p className={`leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{step.description}</p>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-16">
          <a href="/luxury-cars" className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black font-semibold uppercase tracking-wider rounded-full hover:shadow-xl hover:shadow-[#c9a962]/30 transition-all duration-300 hover:scale-105">
            <span>Start Booking Now</span>
            <Icons.ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
export const WhyChooseUs = ({ isDark }) => {
  const features = [
    { icon: '⏰', title: '24×7 Availability', desc: 'Round-the-clock service for your convenience anytime', color: 'from-amber-500 to-orange-500' },
    { icon: '💰', title: 'Transparent Pricing', desc: 'No hidden charges - what you see is what you pay', color: 'from-emerald-500 to-teal-500' },
    { icon: '🚗', title: 'Well Maintained Cars', desc: 'Every car is sanitized and meticulously maintained', color: 'from-blue-500 to-indigo-500' },
    { icon: '✅', title: 'Instant Confirmation', desc: 'Book instantly with immediate confirmation', color: 'from-green-500 to-lime-500' },
    { icon: '🎧', title: 'Premium Support', desc: 'Dedicated customer support team at your service', color: 'from-purple-500 to-violet-500' },
    { icon: '📱', title: 'Easy Booking', desc: 'Simple and quick booking process', color: 'from-pink-500 to-rose-500' },
    { icon: '✨', title: 'Premium Quality', desc: 'Finest luxury vehicles in Udaipur', color: 'from-yellow-500 to-amber-500' },
    { icon: '🏆', title: 'Best Experience', desc: 'Rated best car rental in Udaipur', color: 'from-cyan-500 to-blue-500' }
  ];

  return (
    <section className={`relative py-24 overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative z-10 container-custom">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a962]"></div>
            <span className="text-[#c9a962] font-semibold tracking-wider uppercase text-sm">Why Choose Us</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a962]"></div>
          </div>
          <h2 className={`text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Where <span className="text-[#c9a962]">Excellence</span> Meets <span className="text-[#c9a962]">Reliability</span>
          </h2>
          <p className={`text-lg max-w-2xl mx-auto transition-colors duration-300 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Experience the difference with Udaipur's premier luxury car rental service
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`group relative p-8 rounded-2xl transition-all duration-500 hover:-translate-y-2 ${isDark ? 'bg-[#151515] hover:bg-[#1a1a1a]' : 'bg-white hover:bg-gray-50'} shadow-lg hover:shadow-2xl border border-transparent hover:border-[#c9a962]/30`}
            >
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-500 bg-gradient-to-br ${feature.color}/5`}></div>
              
              <div className="relative mb-6">
                <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300`}>
                  <span className="text-3xl filter drop-shadow-lg">{feature.icon}</span>
                </div>
              </div>
              
              <div className="relative text-center">
                <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 group-hover:text-[#c9a962] ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {feature.title}
                </h3>
                <p className={`text-sm leading-relaxed transition-colors duration-300 ${isDark ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-500 group-hover:text-gray-600'}`}>
                  {feature.desc}
                </p>
              </div>
              
              <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 h-1 w-0 group-hover:w-full rounded-full transition-all duration-500 bg-gradient-to-r ${feature.color}`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
export const TestimonialsSection = ({ isDark }) => {
  const testimonials = [
    { 
      name: 'Rahul Sharma', 
      location: 'Jaipur', 
      rating: 5, 
      comment: 'Excellent service! The BMW was in pristine condition. The team was very professional and the booking process was smooth. Highly recommend Brothers Car Rental for anyone visiting Udaipur!', 
      car: 'BMW 5 Series',
      date: '2 weeks ago',
      avatar: 'R'
    },
    { 
      name: 'Priya Singh', 
      location: 'Mumbai', 
      rating: 5, 
      comment: 'Best car rental experience in Udaipur! The Mercedes was amazing and the customer support was exceptional. Will definitely use their services again on my next visit to the City of Lakes.', 
      car: 'Mercedes-Benz E-Class',
      date: '1 month ago',
      avatar: 'P'
    },
    { 
      name: 'Amit Patel', 
      location: 'Ahmedabad', 
      rating: 5, 
      comment: 'Fantastic luxury cars at affordable prices! The Thar was perfect for our Udaipur trip. Great service, well-maintained vehicles, and friendly staff. What more could you ask for?', 
      car: 'Mahindra Thar',
      date: '3 weeks ago',
      avatar: 'A'
    },
    { 
      name: 'Sneha Gupta', 
      location: 'Delhi', 
      rating: 5, 
      comment: 'Amazing experience with Brothers Car Rental! The Toyota Fortuner was comfortable and clean. The airport pickup service was punctual. Made our family trip to Udaipur truly memorable!', 
      car: 'Toyota Fortuner',
      date: '1 week ago',
      avatar: 'S'
    },
    { 
      name: 'Vikram Joshi', 
      location: 'Pune', 
      rating: 5, 
      comment: 'Professional service from start to finish. The car was delivered on time and in excellent condition. The team went above and beyond to ensure our comfortable journey. Truly premium experience!', 
      car: 'Audi A6',
      date: '2 months ago',
      avatar: 'V'
    },
    { 
      name: 'Ananya Reddy', 
      location: 'Bangalore', 
      rating: 5, 
      comment: 'Best decision to choose Brothers Car Rental! The Honda City was perfect for city drives. Great rates, excellent condition cars, and hassle-free process. Will recommend to everyone!', 
      car: 'Honda City',
      date: '4 weeks ago',
      avatar: 'A'
    }
  ];

  // Calculate average rating
  const avgRating = (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1);
  const totalReviews = 6; // Google Reviews count

  return (
    <section className={`py-12 md:py-24 transition-colors duration-300 ${isDark ? 'bg-[#0d0d0d]' : 'bg-gray-50'}`}>
      <div className="container-custom px-4">
        {/* Header with Google-style Rating Summary */}
        <div className="text-center mb-8 md:mb-16">
          <span className="text-[#c9a962] text-xs md:text-sm font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] animate-pulse">Testimonials</span>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-3 md:mt-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            What Our <span className="text-[#c9a962]">Customers</span> Say
          </h2>
          
          {/* Google-style Rating Summary */}
          <a 
            href="https://www.google.com/search?sca_esv=bca17e01109d9100&sxsrf=ANbL-n5Jf4a_s0rdNMqXOgyrUhdky0CuQg:1770969088093&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOdwvLJ3FF1Mm-3AF5SppO1KHUJxuqHkTJqE_DJnLlo0ZShWf1jrqQEygYmXf-phM-Ml45y2VCMpH9mtZgvAlmB8My5oP&q=Brothers+Rental+Reviews&sa=X&ved=2ahUKEwiBsZ37_dWSAxXyRmwGHYWBCfYQ0bkNegQIRRAF" 
            target="_blank" 
            rel="noopener noreferrer"
            className={`inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 mt-6 md:mt-8 px-4 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${isDark ? 'bg-[#1a1a1a] hover:bg-[#222]' : 'bg-white shadow-lg hover:shadow-xl'}`}
          >
            <div className="text-center">
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#c9a962]">{avgRating}</div>
              <div className="flex items-center justify-center gap-0.5 md:gap-1 mt-1">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 md:w-5 md:h-5 text-[#c9a962]" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <div className={`text-xs md:text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{totalReviews} Google Reviews</div>
            </div>
            <div className={`hidden sm:block h-12 md:h-16 w-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-[#c9a962] to-[#a08040] rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div className="text-left">
                <div className={`text-sm md:text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Verified Reviews</div>
                <div className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>View on Google</div>
              </div>
            </div>
          </a>
        </div>
        
        {/* Testimonials Grid - Google Review Card Style */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index} 
              className={`group relative p-4 md:p-6 rounded-xl md:rounded-2xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#c9a962]/20 ${isDark ? 'bg-[#1a1a1a] hover:bg-[#222]' : 'bg-white shadow-lg hover:shadow-xl'}`}
            >
              {/* Google-style Header */}
              <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-sm md:text-lg font-bold ${isDark ? 'bg-[#c9a962] text-black' : 'bg-gradient-to-br from-[#c9a962] to-[#a08040] text-white'}`}>
                    {testimonial.avatar}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                
                {/* Name & Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 md:gap-2">
                    <h4 className={`text-sm md:text-base font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{testimonial.name}</h4>
                    <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className={`text-xs md:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{testimonial.location} • {testimonial.date}</div>
                </div>
              </div>
              
              {/* Star Rating */}
              <div className="flex items-center gap-0.5 md:gap-1 mb-2 md:mb-3">
                {[...Array(5)].map((_, i) => (
                  <svg 
                    key={i} 
                    className={`w-4 h-4 md:w-5 md:h-5 ${i < testimonial.rating ? 'text-[#c9a962]' : isDark ? 'text-gray-700' : 'text-gray-300'}`} 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
                <span className={`ml-1 md:ml-2 text-xs md:text-sm font-medium ${isDark ? 'text-[#c9a962]' : 'text-[#c9a962]'}`}>{testimonial.rating}.0</span>
              </div>
              
              {/* Review Text */}
              <p className={`mb-3 md:mb-4 leading-relaxed text-xs md:text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{testimonial.comment}</p>
              
              {/* Car Rented Badge */}
              <div className={`inline-flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-full text-[10px] md:text-xs font-medium ${isDark ? 'bg-[#c9a962]/10 text-[#c9a962] border border-[#c9a962]/20' : 'bg-[#c9a962]/10 text-[#a08040] border border-[#c9a962]/20'}`}>
                <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8m-8 4h8m-8 4h8M4 3h16a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z" />
                </svg>
                {testimonial.car}
              </div>
              
              {/* Helpful Button */}
              <div className={`flex items-center justify-between pt-3 md:pt-4 mt-3 md:mt-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                <button className={`flex items-center gap-1.5 md:gap-2 text-xs md:text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-[#c9a962]' : 'text-gray-500 hover:text-[#c9a962]'}`}>
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                  Helpful
                </button>
                <button className={`flex items-center gap-1.5 md:gap-2 text-xs md:text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-[#c9a962]' : 'text-gray-500 hover:text-[#c9a962]'}`}>
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Share
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {/* Write a Review CTA */}
        <div className="text-center mt-8 md:mt-12">
          <a 
            href="https://www.google.com/search?sca_esv=c84920a1f1dac303&sxsrf=ANbL-n6RpP5lSeGGzH6LMzSdrjj4BzXTFg:1770919228858&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOdwvLJ3FF1Mm-3AF5SppO1KHUJxuqHkTJqE_DJnLlo0ZShWf1jrqQEygYmXf-phM-Ml45y2VCMpH9mtZgvAlmB8My5oP&q=Brothers+Rental+Reviews&sa=X&ved=2ahUKEwijnL-cxNSSAxXQga8BHY2IOg4Q0bkNegQIMRAF&biw=1280&bih=678&dpr=1.5" 
            target="_blank" 
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 md:gap-3 px-5 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl text-sm md:text-base font-semibold transition-all duration-300 hover:scale-105 ${isDark ? 'bg-[#1a1a1a] text-white border border-[#c9a962]/30 hover:border-[#c9a962]' : 'bg-white text-gray-900 shadow-lg hover:shadow-xl'}`}
          >
            <svg className="w-5 h-5 md:w-6 md:h-6 text-[#c9a962]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
            </svg>
            Write a Review on Google
            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

// CTA Section
export const CTASection = ({ isDark }) => (
  <section className={`relative py-32 overflow-hidden transition-colors duration-500 ${isDark ? 'bg-gradient-to-br from-[#0a0a0a] via-[#1a1a1a] to-[#0a0a0a]' : 'bg-gradient-to-br from-[#c9a962] via-[#d4b56a] to-[#a08040]'}`}>
    {/* Animated Background Elements */}
    <div className="absolute inset-0 overflow-hidden">
      {/* Floating Car Icons */}
      <div className="absolute top-10 left-10 text-6xl opacity-10 animate-bounce" style={{ animationDuration: '3s' }}>🚗</div>
      <div className="absolute top-20 right-20 text-5xl opacity-10 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>🚙</div>
      <div className="absolute bottom-20 left-1/4 text-4xl opacity-10 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }}>🏎️</div>
      <div className="absolute bottom-10 right-1/3 text-5xl opacity-10 animate-bounce" style={{ animationDuration: '4.5s', animationDelay: '1.5s' }}>🚗</div>
      
      {/* Glowing Orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#c9a962]/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#c9a962]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-[#c9a962]/5 to-transparent rounded-full blur-3xl"></div>
      
      {/* Animated Lines */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent animate-pulse"></div>
      
      {/* Sparkle Effects */}
      <div className="absolute top-1/3 left-1/3 w-2 h-2 bg-white rounded-full animate-ping"></div>
      <div className="absolute top-2/3 right-1/3 w-2 h-2 bg-white rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 right-1/4 w-1 h-1 bg-[#c9a962] rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
    </div>
    
    <div className="relative z-10 container-custom">
      <div className="text-center max-w-4xl mx-auto">
        {/* Badge */}
        <div className={`inline-flex items-center gap-2 px-6 py-2 rounded-full mb-8 ${isDark ? 'bg-[#c9a962]/20 border border-[#c9a962]/30' : 'bg-white/30 border border-white/50'}`}>
          <span className="text-2xl animate-bounce">✨</span>
          <span className={`text-sm font-semibold uppercase tracking-widest ${isDark ? 'text-[#c9a962]' : 'text-gray-900'}`}>Premium Experience Awaits</span>
          <span className="text-2xl animate-bounce" style={{ animationDelay: '0.5s' }}>✨</span>
        </div>
        
        {/* Main Heading */}
        <h2 className={`text-5xl md:text-7xl font-bold mb-6 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Ready to Experience
          <span className="block mt-2">
            <span className={`${isDark ? 'text-[#c9a962]' : 'text-white'} relative`}>
              Luxury
              <svg className="absolute -bottom-2 left-0 w-full h-3" viewBox="0 0 200 12" fill="none">
                <path d="M2 10C50 4 150 4 198 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className={`${isDark ? 'text-[#c9a962]/30' : 'text-white/50'}`}/>
              </svg>
            </span>
            <span className="text-3xl md:text-4xl ml-2">🚗</span>
          </span>
        </h2>
        
        {/* Description */}
        <p className={`text-xl md:text-2xl mb-12 max-w-2xl mx-auto leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-800'}`}>
          Book your premium car today and make your journey unforgettable in the 
          <span className={`font-semibold ${isDark ? 'text-[#c9a962]' : 'text-white'}`}> City of Lakes.</span>
          <br />
          <span className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Available 24/7 for your convenience.</span>
        </p>
        
        {/* CTA Buttons */}
        <div className="flex flex-wrap gap-6 justify-center mb-12">
          <a 
            href="/luxury-cars" 
            className={`group relative px-10 py-5 font-semibold uppercase tracking-wider transition-all duration-500 rounded-2xl overflow-hidden ${isDark ? 'bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black hover:shadow-2xl hover:shadow-[#c9a962]/50' : 'bg-gray-900 text-white hover:bg-gray-800 hover:shadow-2xl'}`}
          >
            <span className="relative z-10 flex items-center gap-3">
              <Icons.Car />
              Browse Cars
              <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-[#a08040] to-[#c9a962]' : 'bg-gray-800'} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
          </a>
          <a 
            href="tel:9602360369" 
            className={`group relative px-10 py-5 font-semibold uppercase tracking-wider transition-all duration-500 rounded-2xl overflow-hidden ${isDark ? 'bg-transparent border-2 border-white text-white hover:bg-white hover:text-black hover:shadow-2xl hover:shadow-white/30' : 'bg-white text-gray-900 border-2 border-gray-900 hover:bg-gray-100 hover:shadow-2xl'}`}
          >
            <span className="relative z-10 flex items-center gap-3">
              <Icons.Phone />
              Call Now
              <span className="text-sm">(96023 60369)</span>
            </span>
          </a>
        </div>
        
        {/* Trust Badges */}
        <div className={`flex flex-wrap justify-center gap-8 pt-8 border-t ${isDark ? 'border-gray-800' : 'border-white/30'}`}>
          <div className="flex items-center gap-2">
            <span className="text-2xl">⭐</span>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-800'}`}>5+ Years Experience</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🚗</span>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-800'}`}>25+ Premium Cars</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">😊</span>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-800'}`}>500+ Happy Customers</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎧</span>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-800'}`}>24/7 Support</span>
          </div>
        </div>
      </div>
    </div>
    
    {/* Decorative Car Image */}
    <div className="absolute bottom-0 right-0 w-1/3 h-full opacity-5 pointer-events-none hidden lg:block">
      <div className="absolute inset-0 bg-gradient-to-l from-transparent to-transparent"></div>
    </div>
  </section>
);
